// src/views/PitaCukai/FormPelekatanPC.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  Input,
  DatePicker,
  Button,
  Typography,
  Space,
  message,
  Modal,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../../configs"; // SESUAIKAN PATH

const { Text } = Typography;
const { Option } = Select;

export default function FormPelekatanPC() {
  const [form] = Form.useForm();

  const [importirOptions, setImportirOptions] = useState([]);
  const [loadingImportir, setLoadingImportir] = useState(false);

  const [allNopenOptions, setAllNopenOptions] = useState([]);
  const [loadingNopen, setLoadingNopen] = useState(false);

  const [loadingDetail, setLoadingDetail] = useState(false);

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // ====== LOAD LIST IMPORTIR (CONTACT) ======
  const fetchImportirList = async () => {
    setLoadingImportir(true);
    try {
      const res = await axiosInstance.get("/contact");
      const list = res.data || [];
      setImportirOptions(
        list
          .filter((c) => c.status === 1 && c.npwp) // optional filter
          .map((c) => ({
            label: `${c.name} (${c.npwp})`,
            value: c.npwp, // <== npwp sebagai value
          }))
      );
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar importir");
    } finally {
      setLoadingImportir(false);
    }
  };

  useEffect(() => {
    fetchImportirList();
  }, []);

  // ====== LOAD LIST NOPEN BERDASARKAN IMPORTIR (id_customer = npwp) ======
  const fetchNopenList = async (idCustomerNpwp) => {
    if (!idCustomerNpwp) {
      setAllNopenOptions([]);
      return;
    }
    setLoadingNopen(true);
    try {
      const res = await axiosInstance.get("/incoming/getListNopenLekat", {
        params: { id_customer: idCustomerNpwp },
      });
      const list = res.data?.data || [];
      setAllNopenOptions(
        list.map((item) => ({
          label: `${item.document_number} - (${dayjs(item.document_date).format(
            "DD-MM-YYYY"
          )})`,
          value: item.document_number,
        }))
      );
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar Nopen");
    } finally {
      setLoadingNopen(false);
    }
  };

  // ====== WATCH groups UNTUK FILTER NOPEN & HITUNG TOTAL ======
  const groupsWatch = Form.useWatch("groups", form) || [];

  const selectedNopens = useMemo(() => {
    const arr = [];
    groupsWatch.forEach((g) => {
      if (g?.nopen) arr.push(g.nopen);
    });
    return arr;
  }, [groupsWatch]);

  // Total kemasan dilekat
  const totalKemasanDilekat = useMemo(() => {
    let tK = 0;
    groupsWatch.forEach((g) => {
      (g?.details || []).forEach((r) => {
        tK += Number(r?.jumlah_kemasan_lekat || 0);
      });
    });
    return tK;
  }, [groupsWatch]);

  // ====== ONCHANGE IMPORTIR ======
  const handleImportirChange = (npwp) => {
    // set id_customer di form (nilai = npwp)
    form.setFieldsValue({ id_customer: npwp, groups: [] });
    setAllNopenOptions([]);
    // load nopen untuk importir ini
    fetchNopenList(npwp);
  };

  // ====== ONCHANGE NOPEN DI TIAP GROUP ======
  const handleChangeNopen = async (noDocument, groupIndex) => {
    if (!noDocument && noDocument !== "") return;

    setLoadingDetail(true);
    try {
      const timestamp = new Date().getTime();
      console.log('🔍 Fetching item-stock with params:', { nopen: noDocument, limit: 10000, _t: timestamp });
      const res = await axiosInstance.get("/item-stock", {
        params: {
          nopen: noDocument,
          limit: 10000,
          _t: timestamp // cache buster
        },
      });
      console.log('✅ Response received:', { total: res.data?.pagination?.total, dataLength: res.data?.data?.length, limit: res.data?.pagination?.limit });

      const rows = res.data?.data || [];

      const mappedDetails = rows.map((d) => ({
        nopen: d.nopen,
        seri: d.seri,
        item_code: d.item_code,
        nama_barang: d.item_name + d.item_code, // bisa diganti kombinasi lain jika ada uraian
        isi_ml: d.isi_ml,
        jumlah_kemasan: d.jumlah_kemasan,
        kemasan_display: d.kemasan || "",
        // diisi user:
        lot_asal: d.lokasi_lot,
        kemasan_lekat: d.kemasan,
        jumlah_kemasan_lekat: undefined,
        lot_tujuan: "",
      }));

      const current = form.getFieldValue("groups") || [];
      current[groupIndex] = {
        ...(current[groupIndex] || {}),
        nopen: noDocument,
        details: mappedDetails,
      };
      form.setFieldsValue({ groups: current });
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil detail Nopen / stripping");
    } finally {
      setLoadingDetail(false);
    }
  };

  // ====== SUBMIT SESUAI PAYLOAD BARU ======
  const handleFinish = async (values) => {
    const groups = values.groups || [];

    if (!groups.length) {
      message.error("Minimal harus ada satu Nopen & detail barang");
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;
    const groups = values.groups || [];
    const nowIso = new Date().toISOString();

    const headerPayload = {
      id_pelekatan_pc: 0,
      id_customer: values.id_customer || 0, // <== pakai npwp importir
      no_ba: values.no_ba,
      tanggal_ba: values.tanggal_ba
        ? dayjs(values.tanggal_ba).format("YYYY-MM-DD")
        : null,
      no_ck1a: values.no_ck1a,
      tanggal_ck1a: values.tanggal_ck1a
        ? dayjs(values.tanggal_ck1a).format("YYYY-MM-DD")
        : null,
      lampiran_1: null,
      // created_at: nowIso,
      // created_by: 0,
      // updated_at: nowIso,
      // updated_by: 0,
    };

    const detailsPayload = [];
    groups.forEach((g) => {
      const nopen = g.nopen || "";
      (g.details || []).forEach((row, idx) => {
        detailsPayload.push({
          id_pelekatan_pc_detail: 0,
          id_pelekatan_pc: 0,
          id_stripping: row.id_stripping || 0,
          nopen,
          seri: row.seri || idx + 1,
          item_code: row.item_code,
          kemasan: row.kemasan || "",
          jumlah_kemasan: Number(row.jumlah_kemasan) || 0,
          lot_asal: row.lot_asal || "",
          kemasan_lekat: row.kemasan_lekat || "",
          jumlah_kemasan_lekat: Number(row.jumlah_kemasan_lekat) || 0,
          lot_tujuan: row.lot_tujuan || "",
          // created_at: nowIso,
          // created_by: 0,
          // updated_at: nowIso,
          // updated_by: 0,
        });
      });
    });

    if (!detailsPayload.length) {
      message.error("Detail barang masih kosong");
      return;
    }

    const payload = {
      header: headerPayload,
      details: detailsPayload,
    };

    try {
      await axiosInstance.post("/pelekatan-pc", payload);
      message.success("Data pelekatan pita cukai berhasil disimpan");

      form.resetFields();
      setConfirmVisible(false);
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan data pelekatan");
    }
  };

  const handleCancel = () => {
    window.history.back();
  };

  return (
    <Card
      className="kt-portlet"
      bodyStyle={{ padding: 24 }}
      style={{
        borderRadius: 16,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        border: "1px solid #F3F6F9",
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          groups: [],
        }}
      >
        {/* ===== HEADER: CK1A & BA & IMPORTIR ===== */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No CK1A</Text>}
              name="no_ck1a"
              rules={[{ required: true, message: "Isi No CK1A" }]}
            >
              <Input placeholder="Masukkan No CK1A" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal CK1A</Text>}
              name="tanggal_ck1a"
              rules={[{ required: true, message: "Pilih tanggal CK1A" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                placeholder="Pilih tanggal"
                format="DD MMMM YYYY"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>BA Pelekatan</Text>}
              name="no_ba"
              rules={[{ required: true, message: "Isi nomor BA Pelekatan" }]}
            >
              <Input placeholder="Nomor BA Pelekatan" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal BA Pelekatan</Text>}
              name="tanggal_ba"
              rules={[{ required: true, message: "Pilih tanggal BA" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                placeholder="Pilih tanggal"
                format="DD MMMM YYYY"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Importir</Text>}
              name="id_customer"
              rules={[{ required: true, message: "Pilih Nama Importir" }]}
            >
              <Select
                placeholder="Pilih Nama Importir"
                loading={loadingImportir}
                options={importirOptions}
                showSearch
                optionFilterProp="label"
                onChange={handleImportirChange}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ===== LIST GROUP NOPEN ===== */}
        <Form.List name="groups">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => {
                const groupIndex = field.name;
                const currentGroup = groupsWatch?.[groupIndex] || {};
                const currentNopen = currentGroup.nopen;

                // Filter opsi nopen: tidak menampilkan yang sudah dipakai group lain
                const availableNopenOptions = allNopenOptions.filter((opt) => {
                  if (opt.value === currentNopen) return true;
                  return !selectedNopens.includes(opt.value);
                });

                return (
                  <Card
                    key={field.key}
                    bodyStyle={{ padding: 0 }}
                    style={{
                      marginTop: 16,
                      borderRadius: 4,
                      border: "1px solid #E4E6EF",
                      overflow: "hidden",
                    }}
                  >
                    {/* HEADER GROUP: NOPEN + HAPUS GROUP */}
                    <div
                      style={{
                        padding: "12px 16px",
                        background: "#F3F6F9",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ flex: 1, paddingRight: 16 }}>
                        <Form.Item
                          {...field}
                          name={[field.name, "nopen"]}
                          fieldKey={[field.fieldKey, "nopen"]}
                          label={
                            <Text strong>
                              Nomor Pendaftaran (NOPEN) - Group{" "}
                              {Number(groupIndex) + 1}
                            </Text>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Pilih Nopen untuk group ini",
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            placeholder="Pilih Nomor Pendaftaran"
                            showSearch
                            optionFilterProp="label"
                            loading={loadingNopen}
                            value={currentNopen}
                            onChange={(val) => {
                              handleChangeNopen(val, groupIndex);
                            }}
                          >
                            {availableNopenOptions.map((opt) => (
                              <Option key={opt.value} value={opt.value}>
                                {opt.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>

                      {/* tombol hapus group nopen */}
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      >
                        Hapus Nopen
                      </Button>
                    </div>

                    {/* TABEL BARANG UNTUK GROUP INI */}
                    <div
                      style={{
                        marginTop: 8,
                      }}
                    >
                      {/* header tabel */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "0.7fr 3fr 2.4fr 2fr 2.4fr 2.4fr 60px",
                          background: "#F3F6F9",
                          padding: "8px 16px",
                          fontWeight: 600,
                          fontSize: 13,
                          alignItems: "center",
                        }}
                      >
                        <div>SERI</div>
                        <div>NAMA BARANG</div>
                        <div>JUMLAH KEMASAN</div>
                        <div>LOT ASAL</div>
                        <div>KEMASAN DILEKAT</div>
                        <div>LOT TUJUAN</div>
                        <div style={{ textAlign: "center" }}>AKSI</div>
                      </div>

                      <Form.List name={[field.name, "details"]}>
                        {(
                          detailFields,
                          { add: addDetail, remove: removeDetail }
                        ) => (
                          <>
                            {detailFields.map((detailField, idx) => (
                              <div key={detailField.key}>
                                {/* hidden untuk payload */}
                                <Form.Item
                                  {...detailField}
                                  name={[detailField.name, "id_stripping"]}
                                  fieldKey={[
                                    detailField.fieldKey,
                                    "id_stripping",
                                  ]}
                                  hidden
                                />
                                <Form.Item
                                  {...detailField}
                                  name={[detailField.name, "seri"]}
                                  fieldKey={[detailField.fieldKey, "seri"]}
                                  hidden
                                />
                                <Form.Item
                                  {...detailField}
                                  name={[detailField.name, "item_code"]}
                                  fieldKey={[detailField.fieldKey, "item_code"]}
                                  hidden
                                />
                                <Form.Item
                                  {...detailField}
                                  name={[detailField.name, "kemasan"]}
                                  fieldKey={[detailField.fieldKey, "kemasan"]}
                                  hidden
                                />

                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      "0.7fr 3fr 2.4fr 2fr 2.4fr 2.4fr 60px",
                                    padding: "8px 16px",
                                    borderTop: "1px solid #E4E6EF",
                                    alignItems: "center",
                                  }}
                                >
                                  {/* SERI (display) */}
                                  <div style={{ paddingRight: 8 }}>
                                    {idx + 1}
                                  </div>

                                  {/* NAMA BARANG */}
                                  <Form.Item
                                    {...detailField}
                                    name={[detailField.name, "nama_barang"]}
                                    fieldKey={[
                                      detailField.fieldKey,
                                      "nama_barang",
                                    ]}
                                    style={{ marginBottom: 0, paddingRight: 8 }}
                                  >
                                    <Input placeholder="NAMA BARANG" disabled />
                                  </Form.Item>

                                  {/* JUMLAH KEMASAN (qty + kemasan) */}
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 4,
                                      paddingRight: 8,
                                    }}
                                  >
                                    <Form.Item
                                      {...detailField}
                                      name={[
                                        detailField.name,
                                        "jumlah_kemasan",
                                      ]}
                                      fieldKey={[
                                        detailField.fieldKey,
                                        "jumlah_kemasan",
                                      ]}
                                      style={{ marginBottom: 0, flex: 1 }}
                                    >
                                      <Input
                                        placeholder="Jlh Kemasan"
                                        disabled
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      {...detailField}
                                      name={[
                                        detailField.name,
                                        "kemasan_display",
                                      ]}
                                      style={{
                                        marginBottom: 0,
                                        width: 70,
                                      }}
                                    // kemasan_display hanya untuk tampil, diisi via initial (kemasan)
                                    >
                                      <Input disabled />
                                    </Form.Item>
                                  </div>

                                  {/* LOT ASAL */}
                                  <Form.Item
                                    {...detailField}
                                    name={[detailField.name, "lot_asal"]}
                                    fieldKey={[
                                      detailField.fieldKey,
                                      "lot_asal",
                                    ]}
                                    style={{ marginBottom: 0, paddingRight: 8 }}
                                  >
                                    <Input placeholder="LOT ASAL" disabled />
                                  </Form.Item>

                                  {/* KEMASAN DILEKAT + QTY DILEKAT */}
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 4,
                                      paddingRight: 8,
                                    }}
                                  >
                                    <Form.Item
                                      {...detailField}
                                      name={[
                                        detailField.name,
                                        "jumlah_kemasan_lekat",
                                      ]}
                                      fieldKey={[
                                        detailField.fieldKey,
                                        "jumlah_kemasan_lekat",
                                      ]}
                                      style={{ marginBottom: 0, flex: 1 }}
                                    >
                                      <Input
                                        placeholder="Jumlah"
                                        inputMode="numeric"
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      {...detailField}
                                      name={[detailField.name, "kemasan_lekat"]}
                                      fieldKey={[
                                        detailField.fieldKey,
                                        "kemasan_lekat",
                                      ]}
                                      style={{ marginBottom: 0, width: 70 }}
                                    >
                                      <Input placeholder="Kemasan" />
                                    </Form.Item>
                                  </div>

                                  {/* LOT TUJUAN */}
                                  <Form.Item
                                    {...detailField}
                                    name={[detailField.name, "lot_tujuan"]}
                                    fieldKey={[
                                      detailField.fieldKey,
                                      "lot_tujuan",
                                    ]}
                                    style={{ marginBottom: 0, paddingRight: 8 }}
                                  >
                                    <Input placeholder="LOT TUJUAN" />
                                  </Form.Item>

                                  {/* HAPUS BARIS BARANG */}
                                  <div style={{ textAlign: "center" }}>
                                    <Button
                                      type="text"
                                      icon={
                                        <DeleteOutlined
                                          style={{ color: "#F64E60" }}
                                        />
                                      }
                                      onClick={() =>
                                        removeDetail(detailField.name)
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </Form.List>
                    </div>
                  </Card>
                );
              })}

              {/* tombol tambah Nopen group */}
              <div style={{ marginTop: 16 }}>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({
                      nopen: null,
                      details: [],
                    })
                  }
                >
                  Tambah Nopen
                </Button>
              </div>
            </>
          )}
        </Form.List>

        {/* ===== TOTALS ===== */}
        <Row style={{ marginTop: 16 }}>
          <Col xs={24} md={6}>
            <Text strong>TOTAL KEMASAN DILEKAT</Text>
            <Input
              value={totalKemasanDilekat}
              disabled
              style={{ marginTop: 4 }}
            />
          </Col>
        </Row>

        {/* ===== ACTION BUTTONS ===== */}
        <Row justify="start" style={{ marginTop: 24 }}>
          <Space>
            <Button onClick={handleCancel}>Batal</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loadingDetail}
              style={{
                background: "#1BC5BD",
                borderColor: "#1BC5BD",
                minWidth: 140,
                fontWeight: 600,
              }}
            >
              Simpan
            </Button>
          </Space>
        </Row>
      </Form>


      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Pelekatan Pita Cukai"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan"
        cancelText="Batal"
        confirmLoading={loadingDetail} // reusing loading state or create new one
        width={600}
      >
        {pendingValues && (
          <div>
            <p>Apakah Anda yakin ingin menyimpan data pelekatan ini?</p>
            <div style={{ background: "#f5f5f5", padding: 10, borderRadius: 4, marginBottom: 16 }}>
              <p style={{ margin: 0 }}>
                <strong>No CK1A:</strong> {pendingValues.no_ck1a}
              </p>
              <p style={{ margin: 0 }}>
                <strong>No BA:</strong> {pendingValues.no_ba}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Importir:</strong> {importirOptions.find(o => o.value === pendingValues.id_customer)?.label || pendingValues.id_customer}
              </p>
            </div>

            <Text strong>Ringkasan Barang:</Text>
            <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8, border: "1px solid #f0f0f0" }}>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead style={{ background: "#fafafa", position: "sticky", top: 0 }}>
                  <tr>
                    <th style={{ padding: "4px 8px", textAlign: "left", borderBottom: "1px solid #f0f0f0" }}>Nopen</th>
                    <th style={{ padding: "4px 8px", textAlign: "left", borderBottom: "1px solid #f0f0f0" }}>Nama Barang</th>
                    <th style={{ padding: "4px 8px", textAlign: "right", borderBottom: "1px solid #f0f0f0" }}>Jml Dilekat</th>
                  </tr>
                </thead>
                <tbody>
                  {(pendingValues.groups || []).flatMap(g =>
                    (g.details || []).map((d, idx) => (
                      <tr key={`${g.nopen}-${idx}`} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "4px 8px" }}>{g.nopen}</td>
                        <td style={{ padding: "4px 8px" }}>{d.nama_barang}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right" }}>{d.jumlah_kemasan_lekat}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </Card >
  );
}
